export interface SupplierSearchMatchInput {
  name: string
  handle: string
  shortName?: string
}

const SupplierMatchRank = {
  exact: 0,
  prefix: 1,
  contains: 2,
  none: 3,
} as const

type SupplierMatchRank = typeof SupplierMatchRank[keyof typeof SupplierMatchRank]

const UMLAUT_MAP: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
}

/**
 * Fold case, German umlauts, other diacritics, and punctuation (SHP-4212).
 * Umlauts expand before NFKD so `Müller` matches `mueller`; NFKD then strips
 * accents such as `é` the same way backoffice `foldSearchText` does.
 */
export function normalizeSupplierMatchText(value: string): string {
  const foldedUmlauts = value
    .toLowerCase()
    .replace(/[äöüß]/g, character => UMLAUT_MAP[character] ?? character)

  return foldedUmlauts
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]/g, '')
}

function normalizedSupplierFields(supplier: SupplierSearchMatchInput): string[] {
  return [supplier.name, supplier.shortName, supplier.handle]
    .filter((field): field is string => Boolean(field))
    .map(normalizeSupplierMatchText)
    .filter(Boolean)
}

export function getSupplierMatchRank(
  query: string,
  supplier: SupplierSearchMatchInput,
): SupplierMatchRank {
  const normalizedQuery = normalizeSupplierMatchText(query)
  if (!normalizedQuery) return SupplierMatchRank.none

  const fields = normalizedSupplierFields(supplier)
  if (fields.includes(normalizedQuery)) return SupplierMatchRank.exact
  if (fields.some(field => field.startsWith(normalizedQuery))) return SupplierMatchRank.prefix
  if (fields.some(field => field.includes(normalizedQuery))) return SupplierMatchRank.contains
  return SupplierMatchRank.none
}

export function supplierMatchesNormalizedQuery(
  supplier: SupplierSearchMatchInput,
  query: string,
): boolean {
  return getSupplierMatchRank(query, supplier) !== SupplierMatchRank.none
}

export function rankSupplierMatches<T extends SupplierSearchMatchInput>(
  query: string,
  suppliers: readonly T[],
): T[] {
  return suppliers
    .map(supplier => ({ supplier, rank: getSupplierMatchRank(query, supplier) }))
    .filter((entry): entry is { supplier: T, rank: Exclude<SupplierMatchRank, 3> } =>
      entry.rank !== SupplierMatchRank.none,
    )
    .sort((left, right) => left.rank - right.rank)
    .map(entry => entry.supplier)
}

/**
 * Unique exact or prefix supplier for typed Enter/Search (SHP-4212).
 * One exact match wins over other prefixes. Several exact matches, or several
 * prefixes with no exact match, stay a normal search. Contains-only hits stay
 * in the dropdown and do not auto-navigate.
 */
export function findSupplierSearchMatch<T extends SupplierSearchMatchInput>(
  query: string,
  suppliers: readonly T[],
): T | null {
  const matches = suppliers
    .map(supplier => ({ supplier, rank: getSupplierMatchRank(query, supplier) }))
    .filter(entry => entry.rank === SupplierMatchRank.exact || entry.rank === SupplierMatchRank.prefix)

  const exact = matches.filter(entry => entry.rank === SupplierMatchRank.exact)
  if (exact.length === 1) return exact[0]?.supplier ?? null
  if (exact.length > 1) return null

  return matches.length === 1 ? matches[0]?.supplier ?? null : null
}
